'use client'

import { forwardRef, ReactNode, useMemo, Fragment } from 'react'
import { ChevronRight, Home } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '../utils/cn'

/**
 * BIRHAUS Page Header Component
 * 
 * Premium page header implementing BIRHAUS principles:
 * - Spanish-first titles and descriptions
 * - 4-3-1 cognitive rule for actions (max 4 primary, 3 secondary, 1 main)
 * - Accessibility compliance with semantic markup
 * - Elegant gradient design with glassmorphism
 * - Breadcrumb navigation with Spanish labels
 * - Progressive disclosure for complex headers
 */

export interface BreadcrumbItem {
  labelEs: string
  labelEn?: string
  href?: string
  icon?: LucideIcon
  current?: boolean
}

export interface BirhausPageHeaderAction {
  labelEs: string
  labelEn?: string
  icon?: LucideIcon
  onClick: () => void | Promise<void>
  variant?: 'primary' | 'secondary' | 'ghost'
  disabled?: boolean
  loading?: boolean
}

export interface BirhausPageHeaderProps {
  // BIRHAUS Principle #7: Bilingual content
  title?: string
  titleEs?: string
  titleEn?: string
  
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Visual elements
  icon?: LucideIcon
  
  // Navigation
  breadcrumbs?: BreadcrumbItem[]
  
  // Actions (BIRHAUS 4-3-1 rule)
  actions?: BirhausPageHeaderAction[]
  
  // Layout variants
  variant?: 'default' | 'minimal' | 'hero' | 'compact'
  
  // BIRHAUS Principle #6: Accessibility
  level?: 1 | 2 | 3 | 4 | 5 | 6
  
  // Locale
  locale?: 'es' | 'en'
  
  // Styling
  className?: string
  gradient?: 'blue' | 'indigo' | 'purple' | 'gray' | 'emerald'
  
  // Custom content
  children?: ReactNode
}

export const BirhausPageHeader = forwardRef<HTMLElement, BirhausPageHeaderProps>(({
  // Content
  title,
  titleEs,
  titleEn,
  description,
  descriptionEs,
  descriptionEn,
  
  // Visual elements
  icon: Icon,
  
  // Navigation
  breadcrumbs = [],
  
  // Actions
  actions = [],
  
  // Layout
  variant = 'default',
  level = 1,
  
  // Locale
  locale = 'es',
  
  // Styling
  className,
  gradient = 'blue',
  
  // Custom content
  children,
  
  ...props
}, ref) => {
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = (
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }

  const displayTitle = getDisplayText(
    title,
    titleEs,
    titleEn,
    'Página'
  )

  const displayDescription = getDisplayText(
    description,
    descriptionEs,
    descriptionEn,
    ''
  )

  // BIRHAUS Validation: Check 4-3-1 cognitive rule for actions
  const actionsCount = actions.length
  if (actionsCount > 4) {
    console.warn(
      `BIRHAUS PageHeader: ${actionsCount} actions exceed the 4-3-1 cognitive rule recommendation of 4. ` +
      'Consider consolidating actions or using a dropdown menu.'
    )
  }

  // Group actions by priority
  const primaryActions = actions.filter(action => action.variant === 'primary' || !action.variant)
  const secondaryActions = actions.filter(action => action.variant === 'secondary')
  const ghostActions = actions.filter(action => action.variant === 'ghost')

  // Validate BIRHAUS 4-3-1 rule more specifically
  if (primaryActions.length > 4) {
    console.warn(`BIRHAUS PageHeader: ${primaryActions.length} primary actions exceed 4-3-1 rule maximum of 4`)
  }
  if (secondaryActions.length > 3) {
    console.warn(`BIRHAUS PageHeader: ${secondaryActions.length} secondary actions exceed 4-3-1 rule maximum of 3`)
  }
  if (ghostActions.length > 1) {
    console.warn(`BIRHAUS PageHeader: ${ghostActions.length} ghost actions exceed 4-3-1 rule maximum of 1`)
  }

  // Gradient configurations
  const gradientClasses = {
    blue: 'from-blue-600 to-blue-700',
    indigo: 'from-indigo-600 to-indigo-700', 
    purple: 'from-purple-600 to-purple-700',
    gray: 'from-gray-600 to-gray-700',
    emerald: 'from-emerald-600 to-emerald-700'
  }

  // Variant styles
  const variantClasses = {
    default: {
      container: 'space-y-6',
      header: 'bg-gradient-to-br rounded-2xl p-6 text-white shadow-lg',
      title: 'text-3xl font-bold',
      description: 'text-white/80 mt-2'
    },
    minimal: {
      container: 'space-y-4',
      header: 'border-b border-gray-200 pb-6',
      title: 'text-2xl font-semibold text-gray-900',
      description: 'text-gray-600 mt-1'
    },
    hero: {
      container: 'space-y-8',
      header: 'bg-gradient-to-br rounded-3xl p-8 text-white shadow-xl',
      title: 'text-4xl md:text-5xl font-bold',
      description: 'text-white/90 mt-3 text-lg'
    },
    compact: {
      container: 'space-y-3',
      header: 'bg-gradient-to-r rounded-lg p-4 text-white shadow-md',
      title: 'text-xl font-semibold',
      description: 'text-white/80 mt-1 text-sm'
    }
  }

  const variantConfig = variantClasses[variant]

  // Generate appropriate heading tag
  const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements

  // Action button component
  const ActionButton = ({ action }: { action: BirhausPageHeaderAction }) => {
    const displayLabel = getDisplayText(
      undefined,
      action.labelEs,
      action.labelEn,
      'Acción'
    )

    const buttonVariantClasses = {
      primary: variant === 'minimal' 
        ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
        : 'bg-white/20 backdrop-blur text-white hover:bg-white/30 focus:ring-white/50',
      secondary: variant === 'minimal'
        ? 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 focus:ring-gray-500'
        : 'bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white/20 focus:ring-white/30',
      ghost: variant === 'minimal'
        ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:ring-gray-500'
        : 'text-white/80 hover:text-white hover:bg-white/10 focus:ring-white/30'
    }

    return (
      <button
        type="button"
        onClick={action.onClick}
        disabled={action.disabled || action.loading}
        className={cn(
          'inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg',
          'focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all duration-200',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          buttonVariantClasses[action.variant || 'primary']
        )}
        aria-label={displayLabel}
      >
        {action.loading && (
          <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
        )}
        {!action.loading && action.icon && (
          <action.icon className="w-4 h-4 mr-2" />
        )}
        {displayLabel}
      </button>
    )
  }

  return (
    <header
      ref={ref}
      className={cn('birhaus-page-header', variantConfig.container, className)}
      {...props}
    >
      {/* Breadcrumb Navigation */}
      {breadcrumbs.length > 0 && (
        <nav 
          className="flex items-center space-x-1 text-sm text-gray-600"
          aria-label={locale === 'es' ? 'Navegación' : 'Navigation'}
        >
          <Home className="w-4 h-4" />
          {breadcrumbs.map((crumb, index) => {
            const displayLabel = getDisplayText(
              undefined,
              crumb.labelEs,
              crumb.labelEn,
              'Página'
            )

            return (
              <Fragment key={index}>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                {crumb.href && !crumb.current ? (
                  <a
                    href={crumb.href}
                    className="hover:text-gray-900 transition-colors font-medium"
                    aria-current={crumb.current ? 'page' : undefined}
                  >
                    {crumb.icon && <crumb.icon className="w-4 h-4 mr-1 inline" />}
                    {displayLabel}
                  </a>
                ) : (
                  <span 
                    className={cn(
                      'font-medium',
                      crumb.current ? 'text-gray-900' : 'text-gray-600'
                    )}
                    aria-current={crumb.current ? 'page' : undefined}
                  >
                    {crumb.icon && <crumb.icon className="w-4 h-4 mr-1 inline" />}
                    {displayLabel}
                  </span>
                )}
              </Fragment>
            )
          })}
        </nav>
      )}

      {/* Main Header */}
      <div className={cn(
        variantConfig.header,
        variant === 'default' || variant === 'hero' || variant === 'compact' 
          ? gradientClasses[gradient] 
          : ''
      )}>
        <div className="flex items-start justify-between gap-6">
          {/* Title and Description */}
          <div className="flex items-start space-x-4 min-w-0 flex-1">
            {/* Icon */}
            {Icon && (
              <div className={cn(
                'flex-shrink-0 rounded-full flex items-center justify-center',
                variant === 'minimal' 
                  ? 'w-10 h-10 bg-gray-100 text-gray-600'
                  : variant === 'hero'
                    ? 'w-16 h-16 bg-white/20 backdrop-blur text-white'
                    : 'w-12 h-12 bg-white/20 backdrop-blur text-white'
              )}>
                <Icon className={cn(
                  variant === 'hero' ? 'w-8 h-8' : 'w-6 h-6'
                )} />
              </div>
            )}
            
            {/* Title and Description */}
            <div className="min-w-0 flex-1">
              <HeadingTag className={cn(variantConfig.title, 'leading-tight')}>
                {displayTitle}
              </HeadingTag>
              {displayDescription && (
                <p className={cn(variantConfig.description, 'leading-relaxed')}>
                  {displayDescription}
                </p>
              )}
              {/* Custom content */}
              {children}
            </div>
          </div>

          {/* Actions */}
          {actions.length > 0 && (
            <div className="flex items-start space-x-3 flex-shrink-0">
              {actions.map((action, index) => (
                <ActionButton key={index} action={action} />
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  )
})

BirhausPageHeader.displayName = 'BirhausPageHeader'

// Hook for managing page header state
export function useBirhausPageHeader() {
  const createBreadcrumb = (
    labelEs: string,
    labelEn?: string,
    href?: string,
    options: Partial<BreadcrumbItem> = {}
  ): BreadcrumbItem => ({
    labelEs,
    labelEn,
    href,
    ...options
  })

  const createAction = (
    labelEs: string,
    onClick: () => void,
    options: Partial<BirhausPageHeaderAction> = {}
  ): BirhausPageHeaderAction => ({
    labelEs,
    onClick,
    variant: 'primary',
    ...options
  })

  return {
    createBreadcrumb,
    createAction,
    
    // Common breadcrumb patterns
    homeBreadcrumb: {
      labelEs: 'Inicio',
      labelEn: 'Home',
      href: '/',
      icon: Home
    }
  }
}

// Common page header configurations
export const birhausPageHeaderPresets = {
  // Dashboard pages
  dashboard: {
    variant: 'hero' as const,
    gradient: 'blue' as const,
    titleEs: 'Panel de Control',
    titleEn: 'Dashboard'
  },
  
  // List pages
  listPage: {
    variant: 'default' as const,
    gradient: 'indigo' as const
  },
  
  // Detail pages  
  detailPage: {
    variant: 'compact' as const,
    gradient: 'gray' as const
  },
  
  // Settings pages
  settings: {
    variant: 'minimal' as const,
    titleEs: 'Configuración',
    titleEn: 'Settings'
  },
  
  // Reports pages
  reports: {
    variant: 'default' as const,
    gradient: 'emerald' as const,
    titleEs: 'Reportes',
    titleEn: 'Reports'
  }
}